import React, { useState } from 'react';
import './DataTable.css';

export default function DataTable({
  columns,
  data,
  onRowClick,
  actions,
  emptyMessage = 'No items found',
  searchable = true,
  searchPlaceholder = 'Search...',
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [viewMode, setViewMode] = useState('grid');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filtered = data.filter((item) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return columns.some((col) => {
      const val = item[col.key];
      return val !== null && val !== undefined && String(val).toLowerCase().includes(q);
    });
  });

  const sorted = sortKey
    ? [...filtered].sort((a, b) => {
        const aVal = a[sortKey] ?? '';
        const bVal = b[sortKey] ?? '';
        const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  return (
    <div className="data-table">
      {/* Toolbar */}
      <div className="data-table__toolbar">
        {searchable && (
          <div className="data-table__search-wrap">
            <svg className="data-table__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              className="data-table__search"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        <div className="data-table__toolbar-right">
          <span className="data-table__count">{sorted.length} items</span>
          <div className="data-table__view-toggle">
            <button
              className={`data-table__view-btn ${viewMode === 'list' ? 'data-table__view-btn--active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
              </svg>
            </button>
            <button
              className={`data-table__view-btn ${viewMode === 'grid' ? 'data-table__view-btn--active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="data-table__empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
            <circle cx="12" cy="12" r="10"/><line x1="8" y1="15" x2="16" y2="15"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
          <p>{emptyMessage}</p>
        </div>
      ) : viewMode === 'list' ? (
        <div className="data-table__wrapper">
          <table className="data-table__table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable !== false && handleSort(col.key)}
                    className={col.sortable !== false ? 'data-table__th--sortable' : ''}
                  >
                    <span>{col.label}</span>
                    {sortKey === col.key && (
                      <span className="data-table__sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                ))}
                {actions && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map((item, i) => (
                <tr
                  key={item.id || i}
                  onClick={() => onRowClick?.(item)}
                  className={onRowClick ? 'data-table__row--clickable' : ''}
                >
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(item[col.key], item) : String(item[col.key] ?? '—')}
                    </td>
                  ))}
                  {actions && (
                    <td className="data-table__actions" onClick={(e) => e.stopPropagation()}>
                      {actions(item)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="data-table__grid">
          {sorted.map((item, i) => (
            <div
              key={item.id || i}
              className="data-table__grid-card"
              onClick={() => onRowClick?.(item)}
            >
              <div className="data-table__grid-card-body">
                {columns.slice(0, 4).map((col) => (
                  <div key={col.key} className="data-table__grid-card-row">
                    <span className="data-table__grid-card-label">{col.label}</span>
                    <span className="data-table__grid-card-value">
                      {col.render ? col.render(item[col.key], item) : String(item[col.key] ?? '—')}
                    </span>
                  </div>
                ))}
              </div>
              {actions && (
                <div className="data-table__grid-card-actions" onClick={(e) => e.stopPropagation()}>
                  {actions(item)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
